import { Action, State, StateContext } from '@ngxs/store';
import { inject, Injectable, NgZone } from '@angular/core';
import { catchError, tap, throwError } from 'rxjs';
import { NotificationService } from '../../app/services/notification.service';
import { ReportStateModel } from './report.model';
import { ReportService } from './report.services';
import {
  ClearReport,
  CreateReport,
  CreateVisualization,
  DeleteReport,
  DeleteVisualization,
  ExportStaticReport,
  GetBagTraceability,
  GetFarmerReceipt,
  GetReceiving,
  GetReconciliation,
  GetReport,
  GetReportList,
  GetTransactionsSummary,
  GetTransferVoucher,
  GetViewReportData,
  GetVisualizationData,
  ScheduleReport,
  UpdateReport,
  UpdateVisualization,
} from './report.actions';
import { Router } from '@angular/router';

@State<ReportStateModel>({
  name: 'Report',
  defaults: {
    farmerReceipt: {
      rows: [],
      total: 0,
    },
    transferVoucher: {
      rows: [],
      total: 0,
    },
    receiving: {
      rows: [],
      total: 0,
    },
    reconciliation: {
      rows: [],
      total: 0,
      totalReceived: 0,
    },
    bagTraceability: {
      rows: [],
      total: 0,
    },
    transactionsSummary: {
      rows: [],
      totalBags: 0,
      totalWeight: 0,
    },
    isProcessing: false,
    reports: [],
    reportData: {
      rows: [],
      aggregation: null,
    },
    report: null,
    pagination: {
      currentPage: 1,
      itemsPerPage: 10,
      totalItems: 0,
      totalPages: 0,
    },
    headers: [],
    reportHeaders: [],
    visualizationData: {},
  },
})
@Injectable({
  providedIn: 'root',
})
export class ReportState {
  private readonly reportService = inject(ReportService);
  private readonly notificationService = inject(NotificationService);
  private readonly ngZone = inject(NgZone);
  private readonly router = inject(Router);

  @Action(GetReportList)
  getReportList({ patchState }: StateContext<ReportStateModel>, action: GetReportList) {
    patchState({ isProcessing: true });
    return this.reportService.getReports(action.payload).pipe(
      tap(async (res) => {
        patchState({
          reports: res.payload,
          pagination: res.pagination,
          headers: res.headers,
          isProcessing: false,
        });
      }),
      catchError(async (_error) => {
        patchState({ isProcessing: false });
      }),
    );
  }

  @Action(GetReport)
  getReport({ patchState }: StateContext<ReportStateModel>, action: GetReport) {
    patchState({ isProcessing: true });
    return this.reportService.getReport(action.id).pipe(
      tap(async (res) => {
        patchState({
          report: res.payload,
          isProcessing: false,
        });
      }),
      catchError(async (_error) => {
        patchState({ isProcessing: false });
      }),
    );
  }

  @Action(GetVisualizationData)
  getVisualizationData(
    { patchState, getState }: StateContext<ReportStateModel>,
    action: GetVisualizationData,
  ) {
    patchState({ isProcessing: true });
    return this.reportService.getVisualizationData(action.reportId, action.visualizationId).pipe(
      tap(async (res) => {
        const state = getState();
        patchState({
          visualizationData: {
            ...state.visualizationData,
            [action.visualizationId]: res.payload,
          },
          isProcessing: false,
        });
      }),
      catchError(async (_error) => {
        patchState({ isProcessing: false });
      }),
    );
  }

  @Action(GetViewReportData)
  getViewReportData({ patchState }: StateContext<ReportStateModel>, action: GetViewReportData) {
    patchState({ isProcessing: true, reportHeaders: [] });
    return this.reportService.getViewReportData(action.id, action.payload).pipe(
      tap(async (res) => {
        patchState({
          reportData: res.payload,
          pagination: res.pagination,
          reportHeaders: res.headers,
          isProcessing: false,
        });
      }),
      catchError(async (_error) => {
        patchState({ isProcessing: false });
      }),
    );
  }

  @Action(GetFarmerReceipt)
  getFarmerReceipt({ patchState }: StateContext<ReportStateModel>, action: GetFarmerReceipt) {
    patchState({
      isProcessing: true,
      reportHeaders: [],
      farmerReceipt: { rows: [], total: 0 },
    });
    return this.reportService.getFarmerReceipt(action.payload).pipe(
      tap(async (res) => {
        patchState({
          farmerReceipt: res.payload,
          pagination: res.pagination,
          reportHeaders: res.headers,
          isProcessing: false,
        });
      }),
      catchError(async (_error) => {
        patchState({ isProcessing: false });
      }),
    );
  }

  @Action(GetTransferVoucher)
  getTransferVoucher({ patchState }: StateContext<ReportStateModel>, action: GetTransferVoucher) {
    patchState({ isProcessing: true, reportHeaders: [] });
    return this.reportService.getTransferVoucher(action.payload).pipe(
      tap(async (res) => {
        patchState({
          transferVoucher: res.payload,
          pagination: res.pagination,
          reportHeaders: res.headers,
          isProcessing: false,
        });
      }),
      catchError(async (_error) => {
        patchState({ isProcessing: false });
      }),
    );
  }

  @Action(GetReceiving)
  getReceiving({ patchState }: StateContext<ReportStateModel>, action: GetReceiving) {
    patchState({ isProcessing: true, reportHeaders: [] });
    return this.reportService.getReceiving(action.payload).pipe(
      tap(async (res) => {
        patchState({
          receiving: res.payload,
          pagination: res.pagination,
          reportHeaders: res.headers,
          isProcessing: false,
        });
      }),
      catchError(async (_error) => {
        patchState({ isProcessing: false });
      }),
    );
  }

  @Action(GetReconciliation)
  getReconciliation({ patchState }: StateContext<ReportStateModel>, action: GetReconciliation) {
    patchState({ isProcessing: true, reportHeaders: [] });
    return this.reportService.getReconciliation(action.payload).pipe(
      tap(async (res) => {
        patchState({
          reconciliation: res.payload,
          pagination: res.pagination,
          reportHeaders: res.headers,
          isProcessing: false,
        });
      }),
      catchError(async (_error) => {
        patchState({ isProcessing: false });
      }),
    );
  }

  @Action(GetBagTraceability)
  getBagTraceability({ patchState }: StateContext<ReportStateModel>, action: GetBagTraceability) {
    patchState({ isProcessing: true, reportHeaders: [] });
    return this.reportService.getBagTraceability(action.payload).pipe(
      tap(async (res) => {
        patchState({
          bagTraceability: res.payload,
          pagination: res.pagination,
          reportHeaders: res.headers,
          isProcessing: false,
        });
      }),
    );
  }

  @Action(GetTransactionsSummary)
  getTransactionsSummary(
    { patchState }: StateContext<ReportStateModel>,
    action: GetTransactionsSummary,
  ) {
    patchState({ isProcessing: true, reportHeaders: [] });
    return this.reportService.getTransactionsSummary(action.payload).pipe(
      tap(async (res) => {
        patchState({
          transactionsSummary: res.payload,
          pagination: res.pagination,
          reportHeaders: res.headers,
          isProcessing: false,
        });
      }),
    );
  }

  @Action(CreateReport)
  createReport({ patchState }: StateContext<ReportStateModel>, action: CreateReport) {
    patchState({ isProcessing: true });
    return this.reportService.createReport(action.payload).pipe(
      tap((_res) => {
        patchState({ isProcessing: false });
        this.notificationService.openSuccessToast('Report created successfully!');
        this.ngZone.run(() => {
          this.router.navigate([`/reports/customized/list`]);
        });
      }),
      catchError((err) => {
        console.error('Error creating report:', err);
        patchState({ isProcessing: false });
        return throwError(() => err);
      }),
    );
  }

  @Action(ScheduleReport)
  scheduleReport({ patchState }: StateContext<ReportStateModel>, action: ScheduleReport) {
    patchState({ isProcessing: true });
    return this.reportService.scheduleReport(action.payload).pipe(
      tap((res) => {
        patchState({ isProcessing: false, report: res.payload });
        this.notificationService.openSuccessToast('Report scheduled successfully!');
      }),
      catchError((err) => {
        console.error('Error scheduling report:', err);
        patchState({ isProcessing: false });
        return throwError(() => err);
      }),
    );
  }

  @Action(CreateVisualization)
  createVisualization({ patchState }: StateContext<ReportStateModel>, action: CreateVisualization) {
    patchState({ isProcessing: true });
    return this.reportService.createVisualization(action.id, action.payload).pipe(
      tap((_res) => {
        patchState({ isProcessing: false });
        this.notificationService.openSuccessToast('Report Visualization created successfully!');
      }),
      catchError((err) => {
        console.error('Error creating report visualization:', err);
        patchState({ isProcessing: false });
        return throwError(() => err);
      }),
    );
  }

  @Action(UpdateReport)
  updateReport({ patchState }: StateContext<ReportStateModel>, action: UpdateReport) {
    patchState({ isProcessing: true });
    return this.reportService.updateReport(action.id, action.payload).pipe(
      tap(async (res) => {
        patchState({
          isProcessing: false,
          report: res.payload,
        });
        this.notificationService.openSuccessToast('Report updated successfully!');
      }),
      catchError(async (error) => {
        patchState({ isProcessing: false });
        return throwError(() => error);
      }),
    );
  }

  @Action(UpdateVisualization)
  updateVisualization({ patchState }: StateContext<ReportStateModel>, action: UpdateVisualization) {
    patchState({ isProcessing: true });
    return this.reportService
      .updateVisualization(action.reportId, action.visualizationId, action.payload)
      .pipe(
        tap(async (_res) => {
          patchState({
            isProcessing: false,
          });
          this.notificationService.openSuccessToast('Chart updated successfully!');
        }),
        catchError(async (error) => {
          patchState({ isProcessing: false });
          return throwError(() => error);
        }),
      );
  }

  @Action(DeleteReport)
  deleteReport({ patchState }: StateContext<ReportStateModel>, action: DeleteReport) {
    patchState({ isProcessing: true });
    return this.reportService.deleteReport(action.id).pipe(
      tap(async () => {
        patchState({
          isProcessing: false,
        });
        this.notificationService.openSuccessToast('Report deleted successfully!');
      }),
      catchError(async (_error) => {
        patchState({ isProcessing: false });
      }),
    );
  }

  @Action(DeleteVisualization)
  deleteVisualization({ patchState }: StateContext<ReportStateModel>, action: DeleteVisualization) {
    return this.reportService.deleteVisualization(action.reportId, action.visualizationId).pipe(
      tap(async () => {
        patchState({
          isProcessing: false,
        });
        this.notificationService.openSuccessToast('Chart deleted successfully!');
      }),
      catchError(async (_error) => {
        patchState({ isProcessing: false });
      }),
    );
  }

  @Action(ClearReport)
  clearReport({ patchState }: StateContext<ReportStateModel>) {
    patchState({
      report: null,
      reportHeaders: [],
      headers: [],
      farmerReceipt: {
        rows: [],
        total: 0,
      },
      receiving: {
        rows: [],
        total: 0,
      },
      transferVoucher: {
        rows: [],
        total: 0,
      },
      reconciliation: {
        rows: [],
        total: 0,
        totalReceived: 0,
      },
      bagTraceability: {
        rows: [],
        total: 0,
      },
      transactionsSummary: {
        rows: [],
        totalBags: 0,
        totalWeight: 0,
      },
    });
  }

  @Action(ExportStaticReport)
  exportStaticReport({ patchState }: StateContext<ReportStateModel>, action: ExportStaticReport) {
    patchState({ isProcessing: true });
    return this.reportService.exportStaticReport(action.reportType, action.filters).pipe(
      tap(async () => {
        patchState({ isProcessing: false });
        this.notificationService.openSuccessToast('Report exported successfully!');
      }),
      catchError(async (_error) => {
        patchState({ isProcessing: false });
        return throwError(() => _error);
      }),
    );
  }
}
